import z from 'zod'
import { censusMemberSchema } from './steps/CensusPlanInfo/schemas'
import { previousPlanSchema } from './steps/QuoteDetails/previousPlanSchema'
import { LEVEL_FUNDED, ACA_FULLY_INSURED, FULLY_INSURED_NON_ACA, SELF_FUNDED } from '../../utils'

export const basicInfoStep1Schema = z.object({
  groupId: z.number({
    invalid_type_error: 'Group is required'
  }),
  effectiveDate: z.string().min(1, 'Effective Date is required'),
  fundingType: z.enum([LEVEL_FUNDED, ACA_FULLY_INSURED, FULLY_INSURED_NON_ACA, SELF_FUNDED]),
  agentOfRecordName: z.string().nullable().optional(),
  agentOfRecordEmailAddress: z.string().email().nullable().optional().or(z.literal('')),
  generalAgencyId: z.number().positive().nullable().optional(),
  generalAgencyName: z.string().nullable().optional(),
  pepmPayable: z.string().nullable().refine((value) => {
    if (!value) {
      return true
    }

    // Convert the string value to a number
    const numStr = value.replace(/[$,]+/g, '')
    const numericValue = Number(numStr);

    // Check if it's a valid number and within the specified range
    return !isNaN(numericValue) && numericValue >= 0 && numericValue <= 90;
  }, {
    message: 'Please enter a valid number between $0 and $90',
  }),
})

export const basicInfoStep2Schema = z.object({
  networkId: z.number({
    invalid_type_error: 'A network is required'
  })
})

export const basicInfoStep3Schema = z.object({
  pharmBenefitsManagerId: z.number({
    invalid_type_error: 'A benefits manager is required'
  })
})

export const basicInfoSchema = z.object({
  step1: basicInfoStep1Schema,
  step2: basicInfoStep2Schema,
  step3: basicInfoStep3Schema
})

export const planInfoStep1Schema = z.object({
  selectedPlans: z
    .array(
      z.object({
        id: z.number()
      })
    )
    .min(1, 'At least one plan is required')
})

export const planInfoStep2Schema = z.record(z.string(),
    z.array(
      z.object({
        id: z.number(),
      })
    )
  )

export const planInfoSchema = z.object({
  step1: planInfoStep1Schema,
  step2: planInfoStep2Schema
})

export const censusInfoSchema = z
  .array(censusMemberSchema)
  .min(1, 'Census info is required')
  .refine((members) => {
    let subscriberCount = members.filter(
      (mem) => mem.relationship === 'employee'
    ).length

    return subscriberCount > 1
  }, 'Census must contain at least 2 subscribers (employees) to continue')

export const quoteDetailsStep1Schema = z.object({
  currentCoverageType: z.enum([LEVEL_FUNDED, ACA_FULLY_INSURED, FULLY_INSURED_NON_ACA, SELF_FUNDED, 'No Current Coverage']),
  plans: z.array(previousPlanSchema)
})

export const quoteDetailsStep2Schema = z.object({
  currentMonthlySpend: z
    .string()
    .nullable()
    .refine((val) => {
      if (!val) {
        return true
      }
      return !isNaN(+val)
    }),
  renewalMonthlySpend: z
    .string()
    .nullable()
    .refine((val) => {
      if (!val) {
        return true
      }
      return !isNaN(+val)
    }),
  experienceFilesUploaded: z.boolean().default(false),
  monthlyInvoiceUploaded: z.boolean().default(false),
  acknowledgment: z.boolean().refine((val) => !!val)
})

export const quoteDetailsSchema = z.object({
  step1: quoteDetailsStep1Schema,
  step2: quoteDetailsStep2Schema
})

export const newQuoteFormSchema = z.object({
  entryId: z.number().nullable(),
  basicInfo: basicInfoSchema,
  planInfo: planInfoSchema,
  censusInfo: censusInfoSchema,
  quoteDetails: quoteDetailsSchema
})
